import type { CapabilitiesSnapshot, MeResponse } from '../../entities/me/me-contract.types';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import type { StaffSPARoutePath } from './staff-spa-route-paths';
import { hasPermission, isJwtPlatformSuperadminSession, isRouteAllowed } from './access';

/**
 * Режим платформы (СА без контекста организации): только поверхность суперадмина, без tenant-разделов.
 * См. исключение «платформенный JWT» в staff-spa-core-rules.mdc — узкий UX-гейт, не подмена RBAC.
 */
export function isStaffPlatformMode(
  me: Pick<MeResponse, 'role' | 'organization_id'> | null | undefined,
  impersonateOrganizationId: number | null | undefined
): boolean {
  if (!isJwtPlatformSuperadminSession(me)) return false;
  if (impersonateOrganizationId != null && impersonateOrganizationId > 0) return false;
  const oid = me?.organization_id;
  if (oid != null && oid > 0) return false;
  return true;
}

/**
 * Пункты левого меню в режиме платформы (ключи как в `routes_allowed` / `StaffSPARoutePath`).
 * Включает глобальное приложение / виджет и LAB без имперсонации — выбор организации на самих экранах.
 */
export const STAFF_PLATFORM_MODE_SIDEBAR_BACKEND_KEYS: ReadonlySet<StaffSPARoutePath> = new Set([
  '/superadmin/dashboard',
  '/platform-organizations',
  '/platform-tenant-requests',
  '/platform-plans',
  '/superadmin/users',
  '/admin-tokens',
  '/audit-log',
  /** Очередь ожидающих авторизацию: вход по звонку ждёт подтверждения СА без имперсонации. */
  '/pending-auth',
  /** Кросс-орг. аудит шлюзов; только просмотр — те же API, что и у СА на сводке платформы. */
  '/audit-payments',
  '/feedback',
  /** Глобальные настройки CRM (ключ `/settings` в capabilities). */
  '/settings',
  '/application',
  '/global-widget-theme',
  '/widget-embed-lab',
  /** ИИ-помощник: отчёты/промты (платформенная страница) и чат без контекста организации. */
  '/superadmin/assistant',
  '/assistant',
  /** Профиль: переход только из левого меню, не из топбара. */
  '/me'
]);

export function isStaffPlatformModeSidebarEntry(backendRouteKey: StaffSPARoutePath): boolean {
  return STAFF_PLATFORM_MODE_SIDEBAR_BACKEND_KEYS.has(backendRouteKey);
}

/**
 * В режиме платформы запрещённые пути → редирект на сводку платформы.
 */
export function isStaffPlatformModeBlockedFrontendPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path.startsWith('/organization')) return true;
  if (path.startsWith('/staff')) return true;
  if (path === ROUTE_PATHS.accessRbac) return true;
  if (path === ROUTE_PATHS.staffDirectory || path.startsWith(`${ROUTE_PATHS.staffDirectory}/`)) return true;
  if (path === '/all-users' || path === '/clients') return true;
  if (path.startsWith('/services-types') || path.startsWith('/services-list') || path.startsWith('/services-spaces')) {
    return true;
  }
  return false;
}

export function staffPlatformModeBlockedRedirectTarget(): string {
  return ROUTE_PATHS.superadminDashboard;
}

/**
 * Экран журнала аудита:
 * - superadmin без org: платформенный аудит
 * - org_main_admin в org: организационный аудит
 * - platform_staff с audit:org_read: организационный аудит (имперсонализация)
 * - platform_staff без org: платформенный аудит
 */
export function canAccessAuditLogPage(
  capabilities: CapabilitiesSnapshot | null | undefined,
  me: Pick<MeResponse, 'role' | 'organization_id'> | null | undefined,
  impersonateOrganizationId: number | null | undefined
): boolean {
  if (!isRouteAllowed(capabilities, '/audit-log')) return false;

  const platformMode = isStaffPlatformMode(me, impersonateOrganizationId);
  if (platformMode) {
    return hasPermission(capabilities, 'platform:audit:view');
  } else {
    const isSuperadmin = me?.role === 'superadmin';
    const isImpersonatingSuper = isSuperadmin && impersonateOrganizationId != null && impersonateOrganizationId > 0;
    const hasOrgAuditRead = hasPermission(capabilities, 'audit:org_read');
    return hasOrgAuditRead || isImpersonatingSuper;
  }
}
